import React from "react"
import { css, useTheme } from "@emotion/react"
import styled from "@emotion/styled"
import CheckboxIcon from "@myvp/shared/src/icons/deprecated-checkbox"
import { CustomEvent } from "@myvp/shared/src/types"
import {
  fontStyleItalicCss,
  Regular14Paragraph,
  Semibold12,
} from "@myvp/shared/src/typography"

type CheckboxSize = "normal" | "small"

interface SharedProps {
  title?: string
  label: React.ReactNode
  className?: string
  name?: string
  checked?: boolean
  disabled?: boolean
  onClick?: (e: CustomEvent) => void
}
interface CheckboxOnlyProps extends SharedProps {
  automationId?: string
  buttonAutomationId?: string
  emptyColor?: "default" | "white"
  italic?: boolean
  onBlur?: React.FocusEventHandler
  onFocus?: React.FocusEventHandler
  ref?: React.RefObject<any>
}

export type CheckboxProps = CheckboxOnlyProps &
  CheckboxButtonProps & {
    variant?: "default" | "button" | "icon"
  }

const Container = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;

  button {
    width: auto;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: transparent;
    border: none;
    padding: unset;
    cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  }
`
const Label = styled.label<{ italic?: boolean; disabled?: boolean }>`
  color: ${(props) => props.theme.palette.content.neutral.default};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  ${(props) =>
    props.italic ? fontStyleItalicCss(props.theme) : "font-style: normal"};
  font-weight: normal;
  margin: 0 4px;
`

const DefaultCheckbox = (props: { checked?: boolean }) => {
  const theme = useTheme()

  return props.checked ? (
    <svg
      focusable="false"
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="10"
        y="10"
        width="20"
        height="20"
        rx="2"
        fill={theme.palette.content.secondary.default}
      />
      <path
        d="M14 20L18 24L26 16"
        stroke={theme.palette.content.neutral.inverse}
        strokeWidth="2"
      />
    </svg>
  ) : (
    <svg
      focusable="false"
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="10.5"
        y="10.5"
        width="19"
        height="19"
        rx="1.5"
        fill={theme.palette.content.neutral.subtle}
        stroke={theme.palette.content.neutral.subtle}
      />
    </svg>
  )
}

const InternalCheckbox = ({
  label,
  title,
  onClick,
  checked,
  disabled,
  emptyColor,
  italic,
  onFocus,
  onBlur,
  className,
  name,
  automationId,
  buttonAutomationId,
  ref,
}: CheckboxOnlyProps) => {
  const id = React.useId()
  const labelId = React.useId()

  return (
    <Container
      className={className}
      data-automationid={automationId}
      disabled={disabled}
    >
      <button
        data-automationid={buttonAutomationId}
        ref={ref}
        type="button"
        role="checkbox"
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled}
        aria-checked={checked}
        onClick={() => {
          onClick?.({
            target: {
              // If you use e.target.name, it won't click correctly when clicking inside of the icon
              name,
              checked: !checked,
              type: "checkbox",
            },
          })
        }}
        id={id}
        aria-labelledby={labelId}
        title={typeof label === "string" ? label : title}
      >
        {emptyColor === "default" ? (
          <DefaultCheckbox checked={checked} />
        ) : (
          <CheckboxIcon
            checked={Boolean(checked)}
            disabled={Boolean(disabled)}
            variant="light"
          />
        )}
      </button>
      <Label id={labelId} htmlFor={id} italic={italic} disabled={disabled}>
        {label}
      </Label>
    </Container>
  )
}

interface CheckboxButtonProps extends SharedProps {
  color?: "default" | "white"
  size?: CheckboxSize
  type?: "button" | "submit" | "reset"
  ref?: React.RefObject<HTMLButtonElement>
}

const _Button = styled.button<{ size?: CheckboxSize }>`
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  outline: none;
  border: none;
  border-radius: 4px;
  min-height: 48px;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 8px 16px;
  word-break: break-word;
  text-align: start;
  width: 100%;
  background-color: ${(props) => props.theme.palette.surface.neutral.selected};
  color: ${(props) => props.theme.palette.content.neutral.default};
  svg {
    margin-inline-end: 16px;
  }
  ${(props) =>
    props.size === "small" &&
    css`
      width: unset;
      min-height: unset;
      height: 24px;
      padding: 2px 8px 2px 2px;
      background: ${props.theme.palette.surface.neutral.selected};
      svg {
        margin-inline-end: 8px;
      }
    `};

  ${(props) =>
    props.color === "white" &&
    css`
      background-color: ${props.theme.palette.surface.neutral.default};
    `}
`

const Button = (props: CheckboxButtonProps) => {
  return (
    <_Button
      {...props}
      role="checkbox"
      aria-checked={props.checked}
      aria-label={typeof props.label === "string" ? props.label : props.title}
      onClick={() => {
        props.onClick?.({
          target: {
            // If you use e.target.name, it won't click correctly when clicking inside of the icon
            name: props.name,
            checked: !props.checked,
            type: "checkbox",
          },
        })
      }}
      className={props.className}
      name={props.name}
      type={props.type}
    >
      <>
        <CheckboxIcon
          checked={Boolean(props.checked)}
          disabled={Boolean(props.disabled)}
          variant="primary"
        />
        {(() => {
          if (typeof props.label === "string") {
            return props.size === "small" ? (
              <Semibold12>{props.label}</Semibold12>
            ) : (
              <Regular14Paragraph>{props.label}</Regular14Paragraph>
            )
          } else {
            return props.label
          }
        })()}
      </>
    </_Button>
  )
}

const Checkbox = (p: CheckboxProps) => {
  const variant = p.variant ?? "default"
  const onClick = p.onClick ?? (() => null)
  const checked = p.checked ?? false
  const italic = p.italic ?? false
  const size = p.size ?? "normal"

  const props = {
    ...p,
    variant,
    onClick,
    checked,
    italic,
    size,
  }
  return props.variant === "default" ? (
    <InternalCheckbox {...props} />
  ) : (
    <Button {...props} />
  )
}

export default styled(Checkbox)``
